import { SyntheticEvent, useEffect, useMemo } from 'react';
import Autocomplete from 'components/Autocomplete';
import { useAppDispatch } from 'store';
import { commonTexts, reviewCyclesTexts } from 'i18n';
import { OwnScorecardsFilters } from 'store/scorecards/own';
import { useSelector, batch } from 'react-redux';
import { useIntl } from 'react-intl';
import {
  selectUserScopedReviewCycles,
  updateUserScopedReviewCycles,
} from 'store/options';
import { UIOption } from 'store/options/interfaces/UIOption';
import FilterBar from 'components/FilterBar';
import {
  compactContainerSx,
  compactFiltersSx,
} from 'pages/MyTeam/Filters/Filters.styled';
import { storeName } from 'store/scorecards/own/config';
import {
  selectFilters,
  updateList,
  clearFilters,
  changeFilter,
} from 'store/scorecards';

interface Props {
  onChange?: (filtersKey: string) => void;
}

const filtersSx = {
  ...compactFiltersSx,
  '& .MuiAutocomplete-root': {
    width: '200px !important',
    minWidth: '200px !important',
  },
};

export default function Filters({ onChange }: Props) {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();
  const additionalOption: UIOption = useMemo(
    () => ({
      id: 'all',
      name: formatMessage(commonTexts.all),
    }),
    [formatMessage],
  );
  const reviewCycles = useSelector(selectUserScopedReviewCycles);
  const options = useMemo(
    () => ({
      reviewCycles: [additionalOption, ...reviewCycles],
    }),
    [additionalOption, reviewCycles],
  );
  const { reviewCycleIds } = useSelector(selectFilters(storeName));
  const showClearButton = reviewCycleIds !== null;
  const onFilterChange =
    (key: keyof OwnScorecardsFilters) =>
    (e: SyntheticEvent, option: UIOption | null) => {
      const value = !option || option?.id === 'all' ? null : option.id;
      if (onChange) {
        onChange(JSON.stringify(value));
      }
      batch(() => {
        dispatch(changeFilter({ store: storeName, data: { key, value } }));
        dispatch(updateList({ store: storeName }));
      });
    };
  const onClearFilters = () =>
    batch(() => {
      dispatch(clearFilters({ store: storeName }));
      dispatch(updateList({ store: storeName }));
    });

  useEffect(() => {
    dispatch(updateUserScopedReviewCycles());
  }, [dispatch]);

  return (
    <FilterBar
      showClearButton={showClearButton}
      onClearFilters={onClearFilters}
      sx={compactContainerSx}
      filtersSx={filtersSx}
      iconOnlyReset
    >
      <Autocomplete
        size="large"
        options={options.reviewCycles}
        value={
          options.reviewCycles.find(({ id }) => id === reviewCycleIds) || null
        }
        onChange={onFilterChange('reviewCycleIds')}
        placeholder={formatMessage(reviewCyclesTexts.singleTitle)}
      />
    </FilterBar>
  );
}
